import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import PlayTriangleIcon from '../../../assets/icons/PlayTriangleIcon';
import PauseIcon from '../../../assets/icons/PauseIcon';
import DownloadIcon from '../../../assets/icons/DownloadIcon';
import {saveAs} from 'file-saver';
import Tooltip from '../Tooltip';
import './styles.css';

/*
 * A voice message or an audio file, in the app's own shapes.
 *
 * It replaces a bare <audio controls>, which is the browser's built-in player:
 * a grey slab that looks different in every browser, carries a volume slider
 * and a download menu nobody needs beside a message, and cannot be styled at
 * all - its controls live in a shadow tree that CSS does not reach, and the
 * one hook that exists is Chrome's alone.
 *
 * The <audio> element is still doing the work; only its controls are ours.
 */
const format = (seconds) => {
    if (!seconds || !isFinite(seconds)) return '0:00';
    const total = Math.floor(seconds);
    const mins = Math.floor(total / 60);
    const secs = total % 60;
    return mins + ':' + (secs < 10 ? '0' : '') + secs;
};

/* a voice message's name often has no extension - the file's type says which one */
const withExtension = (name, mimeType) => {
    if (/\.[a-z0-9]+$/i.test(name) || !mimeType) return name;
    const subtype = mimeType.split('/')[1].split(';')[0];
    const extension = {mpeg: 'mp3', 'x-wav': 'wav', mp4: 'm4a', 'x-m4a': 'm4a'}[subtype] || subtype;
    return `${name}.${extension}`;
};

const AudioPlayer = ({src, downloadName}) => {
    const audioRef = useRef(null);
    const [playing, setPlaying] = useState(false);
    const [elapsed, setElapsed] = useState(0);
    const [duration, setDuration] = useState(0);
    const [downloading, setDownloading] = useState(false);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return undefined;

        const onTime = () => setElapsed(audio.currentTime);
        /*
         * Two events, not one. A file served without a Content-Length has no
         * duration at loadedmetadata - it arrives later, or not until the end -
         * so durationchange is what actually fills the total in.
         */
        const onDuration = () => setDuration(audio.duration);
        const onEnd = () => {
            setPlaying(false);
            setElapsed(0);
            audio.currentTime = 0;
        };
        const onPlay = () => setPlaying(true);
        const onPause = () => setPlaying(false);

        audio.addEventListener('timeupdate', onTime);
        audio.addEventListener('loadedmetadata', onDuration);
        audio.addEventListener('durationchange', onDuration);
        audio.addEventListener('ended', onEnd);
        audio.addEventListener('play', onPlay);
        audio.addEventListener('pause', onPause);

        return () => {
            audio.removeEventListener('timeupdate', onTime);
            audio.removeEventListener('loadedmetadata', onDuration);
            audio.removeEventListener('durationchange', onDuration);
            audio.removeEventListener('ended', onEnd);
            audio.removeEventListener('play', onPlay);
            audio.removeEventListener('pause', onPause);
        };
    }, [src]);

    const toggle = () => {
        const audio = audioRef.current;
        if (!audio) return;

        if (audio.paused) {
            /* play() rejects when the browser refuses autoplay, or on an
               unreachable file - unhandled it surfaces as a console error */
            const started = audio.play();
            if (started && started.catch) started.catch(() => setPlaying(false));
        } else {
            audio.pause();
        }
    };

    const seek = (event) => {
        const audio = audioRef.current;
        if (!audio || !duration || !isFinite(duration)) return;

        const bar = event.currentTarget.getBoundingClientRect();
        const ratio = Math.min(Math.max((event.clientX - bar.left) / bar.width, 0), 1);
        audio.currentTime = ratio * duration;
        setElapsed(audio.currentTime);
    };

    /*
     * Fetched and saved as a blob: the file lives on another origin, where a
     * link's `download` attribute is ignored and the browser just plays the
     * file in a new tab. When the fetch itself is refused (CORS), that new tab
     * is still the way to get it.
     */
    const download = () => {
        setDownloading(true);
        fetch(src)
            .then((response) => {
                if (!response.ok) throw new Error(response.status);
                return response.blob();
            })
            .then((blob) => saveAs(blob, withExtension(downloadName, blob.type)))
            .catch(() => window.open(src, '_blank', 'noopener,noreferrer'))
            .then(() => setDownloading(false));
    };

    const progress = duration && isFinite(duration) ? (elapsed / duration) * 100 : 0;

    return (
        <div className="audio-player">
            <audio ref={audioRef} preload="metadata">
                <source src={src}/>
            </audio>

            <button
                type="button"
                className="audio-player__button"
                onClick={toggle}
                aria-label={playing ? 'Pause' : 'Play'}
            >
                {playing
                    ? <PauseIcon width={14} height={14}/>
                    : <PlayTriangleIcon width={14} height={14}/>}
            </button>

            <div className="audio-player__track" onClick={seek}>
                <span className="audio-player__fill" style={{width: progress + '%'}}/>
            </div>

            <span className="audio-player__time">
                {format(elapsed)} / {format(duration)}
            </span>

            {downloadName && (
                <Tooltip text="download" deltaY={-7}>
                    <button
                        type="button"
                        className="audio-player__download"
                        onClick={download}
                        disabled={downloading}
                        aria-label="Download"
                    >
                        <DownloadIcon width={16} height={16} stroke="currentColor"/>
                    </button>
                </Tooltip>
            )}
        </div>
    );
};

AudioPlayer.propTypes = {
    src: PropTypes.string.isRequired,
    /* shows a download button, saving the file under this name */
    downloadName: PropTypes.string,
};

export default AudioPlayer;



// WEBPACK FOOTER //
// ./src/components-new/general/AudioPlayer/index.jsx